import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

export default function WebsiteTerms() {
  return (
    <div data-testid="website-terms-page" className="pt-24 md:pt-32 bg-black min-h-screen">
      <Helmet>
        <title>Website Design Packages | Pricing from £299 | Barton</title>
        <meta name="description" content="Transparent pricing for professional website design. Choose from our £299, £399, or £499 packages. No hidden fees. See what's included." />
      </Helmet>
      <div className="container-custom py-12 md:py-20">
        <div className="max-w-3xl mx-auto">
          <h1 className="font-heading text-3xl md:text-4xl font-bold mb-8 text-white">
            Website Design Services - Terms and Conditions
          </h1>
          
          <div className="prose prose-invert max-w-none text-white/80 space-y-8">
            <p className="text-white/60 text-sm">
              Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">1. Website Design Packages</h2>
              <p className="mb-4">Barton (trading name of VZV Ltd) offers the following website design packages:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong className="text-white">£299 Package:</strong> Up to 5 pages</li>
                <li><strong className="text-white">£399 Package:</strong> Up to 7 pages</li>
                <li><strong className="text-white">£499 Package:</strong> Up to 10 pages</li>
              </ul>
              <p className="mt-4">
                All prices are exclusive of VAT where applicable. A page is defined as a single webpage with standard content including text, images, and basic functionality.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">2. What's Included</h2>
              <p className="mb-4">Each package includes:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Custom website design</li>
                <li>Mobile responsive layout</li>
                <li>Basic SEO setup</li>
                <li>Contact form integration</li>
                <li>Social media links</li>
                <li>Initial content upload (client to provide content)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">3. Hosting Services</h2>
              <p className="mb-4">
                Website hosting is available as an optional service at <strong className="text-white">£29.99 per month plus VAT</strong>. 
                This covers:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Web hosting and server maintenance</li>
                <li>SSL certificate</li>
                <li>Regular backups</li>
                <li>Technical support</li>
              </ul>
              <p className="mt-4">
                Hosting fees are billed monthly in advance. Failure to pay hosting fees may result in the website being taken offline.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">4. Additional Services</h2>
              <p className="mb-4">
                Additional services may be added to any package and will be quoted based on specific requirements. These include but are not limited to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Email hosting and setup</li>
                <li>Domain registration and management</li>
                <li>E-commerce functionality</li>
                <li>Additional pages beyond package allowance</li>
                <li>Custom functionality and integrations</li>
                <li>Ongoing maintenance and updates</li>
                <li>Content writing and copywriting</li>
                <li>Photography and graphic design</li>
              </ul>
              <p className="mt-4">
                All additional services will be quoted in writing before work commences and must be agreed upon by both parties.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">5. Payment Terms</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>A 50% deposit is required before work commences</li>
                <li>The remaining 50% is due upon completion, before the website goes live</li>
                <li>For hosting services, payment is due monthly in advance</li>
                <li>Late payments may incur interest at 4% above the Bank of England base rate</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">6. Client Responsibilities</h2>
              <p className="mb-4">The client agrees to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide all content (text, images, logos) in a timely manner</li>
                <li>Respond to requests for feedback within 7 working days</li>
                <li>Ensure all provided content does not infringe on any third-party rights</li>
                <li>Provide accurate information for contact forms and business details</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">7. Revisions and Amendments</h2>
              <p>
                Each package includes up to 2 rounds of revisions. Additional revisions beyond this allowance 
                will be charged at our standard hourly rate. Major changes to the agreed design brief may 
                require a revised quote.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">8. Intellectual Property</h2>
              <p>
                Upon full payment, the client will own the rights to the final website design. Barton retains 
                the right to display the work in our portfolio and marketing materials unless otherwise agreed 
                in writing. Any third-party assets (stock images, fonts, plugins) remain subject to their 
                respective licences.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">9. Right to Refuse or Cancel Services</h2>
              <p className="mb-4">
                <strong className="text-white">Barton reserves the right to refuse or cancel any project at our discretion.</strong> This includes, but is not limited to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Projects involving illegal, harmful, or offensive content</li>
                <li>Clients who fail to provide required materials or feedback</li>
                <li>Clients who engage in abusive or unreasonable behaviour</li>
                <li>Projects that fall outside our area of expertise</li>
                <li>Non-payment or failure to adhere to payment terms</li>
              </ul>
              <p className="mt-4">
                In the event of cancellation by Barton, any deposits paid will be refunded on a pro-rata basis 
                for work not yet completed. In the event of cancellation by the client, deposits are non-refundable 
                and any work completed will be invoiced.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">10. Limitation of Liability</h2>
              <p>
                Barton shall not be liable for any indirect, incidental, special, or consequential damages 
                arising out of or in connection with our services. Our total liability shall not exceed the 
                total amount paid by the client for the specific service in question. We are not responsible 
                for any loss of business, revenue, or data arising from the use of our services.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">11. Confidentiality</h2>
              <p>
                Both parties agree to keep confidential any proprietary information received during the course 
                of the project. This obligation shall survive the termination of any agreement between the parties.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">12. Force Majeure</h2>
              <p>
                Neither party shall be liable for any failure or delay in performing their obligations where 
                such failure or delay results from circumstances beyond the reasonable control of that party, 
                including but not limited to acts of God, natural disasters, war, terrorism, or pandemic.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">13. Governing Law</h2>
              <p>
                These terms and conditions shall be governed by and construed in accordance with the laws of 
                England and Wales. Any disputes arising shall be subject to the exclusive jurisdiction of the 
                courts of England and Wales.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">14. Changes to Terms</h2>
              <p>
                Barton reserves the right to update these terms and conditions at any time. Any changes will 
                be posted on this page with an updated revision date. Continued use of our services constitutes 
                acceptance of any updated terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">15. Contact Information</h2>
              <p>
                If you have any questions about these terms and conditions, please contact us:
              </p>
              <ul className="list-none mt-4 space-y-1">
                <li><strong className="text-white">Email:</strong> hello@barton.uk</li>
                <li><strong className="text-white">Phone:</strong> 01704 777881</li>
                <li><strong className="text-white">Company:</strong> VZV Ltd (Company No. 10689932)</li>
              </ul>
            </section>

            <div className="mt-12 pt-8 border-t border-white/10">
              <Link 
                to="/contact" 
                className="text-pink-400 hover:text-pink-400/80 transition-colors"
              >
                ← Back to Contact
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
